import { Pipe, PipeTransform } from '@angular/core';
import DOMPurify from 'dompurify';
import { Marked } from 'marked';
import { ALLOWED_TAGS, AllowedTag } from './allowed-tags';

// Escapes all five HTML special characters in the correct order (&amp; first).
function escapeHtml(text: string): string {
    return text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

@Pipe({ name: 'markdown', standalone: true })
export class MarkdownPipe implements PipeTransform {
    private readonly marked: Marked;

    constructor() {
        this.marked = new Marked({
            breaks: true,
            async: false, // ensures that parse() always returns a string
            walkTokens: (token) => {
                if (token.type !== 'html') return;
                // Extract tag names (ignores leading slash for closing tags).
                // walkTokens runs after tokenization, so code spans/blocks
                // are already isolated as their own token types and are not touched.
                const tagMatch = token.text.match(/^<\/?([a-zA-Z][a-zA-Z0-9-]*)/);
                const tagName = tagMatch?.[1]?.toLowerCase() ?? '';
                if (!ALLOWED_TAGS.includes(tagName as AllowedTag)) {
                    // marked's block HTML rule (type 6) absorbs all following lines until a blank line
                    // into the same html token. After escaping, those \n would render as spaces in the
                    // browser. We replace them with <br> to mirror what breaks:true does for paragraphs.
                    token.text = escapeHtml(token.text).replaceAll('\n', '<br>\n');
                }
            },
        });
    }

    transform(value: string): string {
        if (!value) {
            return '';
        }

        const dirtyHtml = this.marked.parse(value) as string;
        return DOMPurify.sanitize(dirtyHtml.trim(), {
            ALLOWED_TAGS: [...ALLOWED_TAGS],
            ALLOWED_ATTR: [], // No attributes allowed — prevents style/class injection
        });
    }
}
